import * as XLSX from 'xlsx';
import { Empty } from 'antd';
import { createPortal } from 'react-dom';
import tableDesign from 'themes/table';
import Table from '@mui/material/Table';
import MainCard from 'components/MainCard';
import TableRow from '@mui/material/TableRow';
import { useLocation } from 'react-router-dom';
import TableCell from '@mui/material/TableCell';
import { useTheme } from '@mui/material/styles';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import TableContainer from '@mui/material/TableContainer';
import ExportButton from 'components/button/ExportButton';
import ColumnFilter from 'components/@extended/ColumnFilter';
import { Box, Button, useMediaQuery, Tooltip } from '@mui/material';
import SortingIconGroup from 'components/@extended/SortingIconGroup';
import TablePagination from 'components/LynDataTable/DataTablePagination';
import SelectVisibleColumn from 'components/@extended/SelectVisibleColumn';
import { useFilters, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';
import { removeDataTableSearchKey, setDataTableInfo, setScrollIntoTableRow } from 'store/reducer/mainSlice';

const tableVariants = {
	default: {
		table: {
			// border: '1px solid',
			// borderColor: tableDesign.borderColor.primary,

			'& td, th': {
				borderRight: '1px solid',
				borderColor: tableDesign.borderColor.primary,
			},

			'& th': {
				'&:first-of-type': { borderLeft: `1px solid ${tableDesign.borderColor.primary}` },
			},
		},
		tableMainCard: {
			mt: 2,
			borderRadius: tableDesign.borderRadius.primary,
			borderColor: tableDesign.borderColor.primary,
			border: 'none',
		},
		tableContainer: (pagination) => ({
			width: '100%',
			display: 'block',
			overflowX: 'auto',
			position: 'relative',
			// mb: pagination ? '36px' : '0px',
			mb: pagination ? '44px' : '0px',
			maxHeight: `calc(100svh - 358px)`,

			'& td, & th': { whiteSpace: 'nowrap' },
		}),
		tableBodyCell: (cell) => ({
			padding: tableDesign.padding.primary,
			borderBottom: '1px solid #E0E0E0',
			color: tableDesign.color.secondary,

			'&:first-of-type': { borderLeft: '1px solid', borderColor: tableDesign.borderColor.primary },

			...(cell?.column?.sticky === 'right' && {
				right: 0,
				zIndex: 1,
				position: 'sticky',
				backgroundColor: tableDesign.background.primary,
				boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
			}),
		}),
		tableBodyCellBox: (cell) => ({
			padding: 0,
			display: 'block',
			maxWidth: '337px',
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
			fontSize: tableDesign.fontSize.primary,
			fontWeight: tableDesign.fontWeight.primary,
			textAlign: cell?.column?.cellAlign || 'left',
			color: tableDesign.color.secondary,

			...(cell?.column?.cellAlign === 'right' ? { fontVariantNumeric: 'tabular-nums' } : {}),
		}),
		tableHeaderCell: (column) => ({
			p: 0,
			color: tableDesign.color.primary,
			backgroundColor: tableDesign.background.primary,

			...(column?.sticky === 'right' && {
				right: 0,
				zIndex: 2,
				position: 'sticky',
				// borderLeft: '1px solid #fff',
			}),
		}),
		tableHeaderCellBox: (column) => ({
			display: 'flex',
			alignItems: 'center',
			padding: tableDesign.padding.secondary,
			fontSize: tableDesign.headerFontSize.primary,
			fontWeight: tableDesign.headerFontWeight.primary,
			justifyContent: column?.align ? column?.align : 'space-between',
		}),

		tableHeaderRow: { height: '40px' },
		tableBodyRow: { cursor: 'pointer' },
		emptyTableBox: {
			width: '100%',
			height: '350px',
			display: 'flex',
			alignItems: 'center',
			flexDirection: 'column',
			justifyContent: 'center',
		},
		paginationBox: {
			left: 0,
			bottom: 0,
			width: '100%',
			position: 'absolute',
			backgroundColor: tableDesign.background.primary,
		},
		pagination: {
			'&.Mui-disabled': { color: 'red' },
			'&.MuiTablePagination-root': { color: tableDesign.color.primary },
			'.MuiTablePagination-selectIcon': { color: tableDesign.color.primary },
		},
	},
};

const ComptrollerDataTable = ({
	DATA = [],
	COLUMNS = [],
	deleteHandle,
	actionButtons,
	selectedTabName,
	balkAction = true,
	rowSelection = [],
	bulkDelete = true,
	screen = 'default',
	variant = 'default',
	customButton = null,
	exportButton = true,
	columnButton = true,
	externalToolbarHost = null,
	xlsxFileName = 'lynorg',
	submitHandle = () => {},
	setFilterData = () => {},
	paginationEnabled = true,
	onClickHandler = () => {},
	setRowSelection = () => {},
	activeDeactivateHandle = () => {},

	hoveredRow = null,
	onRowHover = () => {},
	matchingSerialNumbers = [],
	enableHoverHighlight = false,
	secondaryMatchingSerialNumbers,
}) => {
	const theme = useTheme();
	const location = useLocation();
	const dispatch = useDispatch();

	const { pathname } = location;

	const matchDownSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));

	const limit = useSelector((state) => state.main.limit);
	const pageIndex = useSelector((state) => state.main.page);
	const clickedRow = useSelector((state) => state.main.clickedItem);

	const data = useMemo(() => DATA, [DATA]);
	const columns = useMemo(() => COLUMNS, [COLUMNS]);

	const defaultColumn = useMemo(() => {
		return {
			Filter: ColumnFilter,
			sortDescFirst: true,
		};
	}, []);

	const defaultHiddenColumns = useMemo(() => {
		return columns
			.filter((column) => column?.isVisible === false)
			.map((column) => (typeof column.accessor === 'string' ? column.accessor : column.id));
	}, [columns]);

	let tableInstanceOption = {};

	tableInstanceOption = {
		columns,
		data,
		defaultColumn,
		autoResetPage: true,
		disablePagination: !paginationEnabled,
		initialState: {
			pageIndex: 0,
			pageSize: 15,
			hiddenColumns: defaultHiddenColumns,
		},
	};

	const tableInstance = useTable({ ...tableInstanceOption }, useFilters, useSortBy, usePagination, useRowSelect);

	const {
		rows,
		page,
		state,
		gotoPage,
		prepareRow,
		allColumns,
		setPageSize,
		filteredRows,
		headerGroups,
		getTableProps,
		selectedFlatRows,
		getTableBodyProps,
		getToggleHideAllColumnsProps,
	} = tableInstance;

	const rowsToRender = paginationEnabled ? page : rows;

	const dataExport = useCallback(() => {
		const headerArr = headerGroups[0].headers.map((item) => item.Header).filter((item) => item !== 'Action' && typeof item !== 'function');

		const rowsArr = rows.map((item) => {
			const disableColumnKey = [...state.hiddenColumns, 'Selection', 'Action'];

			const obj = { ...item.values };

			disableColumnKey.map((key) => {
				delete obj[key];

				return delete obj[key];
			});

			return Object.values(obj);
		});

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.json_to_sheet([]);
		XLSX.utils.sheet_add_aoa(ws, [headerArr]);
		XLSX.utils.sheet_add_json(ws, rowsArr, { origin: 'A2', skipHeader: true });

		Object.keys(ws).forEach((cell) => {
			if (cell[0] === '!') return;

			const cellObj = ws[cell];

			if (typeof cellObj.v === 'string') {
				const cleaned = cellObj.v.replace(/,/g, '');

				if (!isNaN(cleaned)) {
					cellObj.v = Number(cleaned);
					cellObj.t = 'n';
					cellObj.z = '#,##0.00';
				}
			}
		});

		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
		XLSX.writeFile(wb, `${xlsxFileName}.xlsx`);
	}, [state, rows, headerGroups]);

	const memorizeClickedItem = (id) => {
		dispatch(setScrollIntoTableRow({ clickedItem: { [pathname]: id } }));
		if (paginationEnabled) {
			dispatch(
				setDataTableInfo({
					page: { [pathname]: state?.pageIndex },
					limit: { [pathname]: state?.pageSize },
				})
			);
		}
	};

	useEffect(() => {
		if (paginationEnabled) {
			if (state.filters.length === 0) {
				gotoPage(pageIndex?.[pathname] || 0);
			}
			setPageSize(limit?.[pathname] || 15);
		}
	}, [paginationEnabled]);

	useEffect(() => {
		if (state?.filters.length > 0) {
			if (filteredRows?.length > 0) {
				const filterData = filteredRows?.map((row) => row?.original);

				setFilterData(filterData);
			}
		} else {
			setFilterData(data);
		}
	}, [state?.filters]);

	useEffect(() => {
		const hasChanged =
			selectedFlatRows?.length !== rowSelection?.length ||
			selectedFlatRows.some((row, index) => row.id !== rowSelection[index]?.id || row.original?.id !== rowSelection[index]?.original?.id);

		if (hasChanged) {
			setRowSelection(selectedFlatRows);
		}
	}, [selectedFlatRows, rowSelection]);

	useEffect(() => {
		if (state.filters.length === 0) {
			gotoPage(pageIndex?.[pathname] || 0);
		}

		setPageSize(limit?.[pathname] || 15);
	}, []);

	useEffect(() => {
		const id = clickedRow[pathname];

		if (id) {
			const project = document.getElementById(id);

			if (project) {
				project.classList.add('active');
				project.scrollIntoView({ behavior: 'smooth', block: 'center' });
				dispatch(setScrollIntoTableRow({ clickedItem: {} }));
			}
		}
	}, [allColumns, state.pageIndex, state.pageSize]);

	useEffect(() => {
		const currentPathName = window.location.pathname;

		return () => {
			const destinationPathName = window.location.pathname;

			if (!destinationPathName.includes(currentPathName)) {
				dispatch(removeDataTableSearchKey({ currentPathName }));
			}
		};
	}, [location]);

	const renderActionButtons = () => {
		if (actionButtons !== undefined) {
			return (
				<Box
					content="div"
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						alignItems: 'center',
						gap: { xs: '6px', md: '8px' },
					}}
				>
					{actionButtons.map((button, index) => (
						<Tooltip key={index} title={button.disabled ? button.disabledTextTooltip : button.title}>
							<Button
								type="button"
								disabled={button.disabled}
								sx={{
									py: '6px',
									fontSize: '13px',
									lineHeight: '22px',
									fontWeight: '500',
									borderRadius: '4px',
									transition: 'all 0.1s',
									backgroundColor: button.disabled ? 'gray' : button.color,
									color: button.disabled ? 'white' : (button?.textColor ?? '#000000'),

									'&:hover': {
										backgroundColor: button.disabled ? 'gray' : button.color,
										color: button.disabled ? 'white' : (button?.textColor ?? '#000000'),
									},
								}}
								onClick={(e) => {
									e.stopPropagation();
									if (!button.disabled && typeof button.onClick === 'function') {
										button.onClick();
									}
								}}
							>
								{rowSelection?.length > 1 ? button.title + ' all' : button.title}
							</Button>
						</Tooltip>
					))}
				</Box>
			);
		}

		if (customButton && customButton.length > 0) {
			return (
				<Box
					content="div"
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						alignItems: 'center',
						gap: { xs: '6px', md: '8px' },
					}}
				>
					{customButton.map((button, index) => (
						<Tooltip key={index} title={button.disabled ? button.disabledTextTooltip : button.title}>
							<Button
								type="button"
								disabled={button.disabled}
								sx={{
									py: '6px',
									fontSize: '13px',
									lineHeight: '22px',
									fontWeight: '500',
									borderRadius: '4px',
									transition: 'all 0.1s',
									backgroundColor: button.disabled ? 'gray' : button.color,
									color: button.disabled ? 'white' : (button?.textColor ?? '#000000'),

									'&:hover': {
										backgroundColor: button.disabled ? 'gray' : button.color,
										color: button.disabled ? 'white' : (button?.textColor ?? '#000000'),
									},
								}}
								onClick={() => {
									if (!button.disabled && typeof button.onClick === 'function') {
										button.onClick();
									}
								}}
							>
								{rowSelection?.length > 1 ? button.title + ' all' : button.title}
							</Button>
						</Tooltip>
					))}
				</Box>
			);
		}

		return (
			<Box
				content="div"
				sx={{
					display: 'flex',
					flexWrap: 'wrap',
					alignItems: 'center',
					gap: { xs: '6px', md: '8px' },
				}}
			>
				{!['Active', 'Worklist', 'Drafts']?.includes(selectedTabName) && (
					<Tooltip title={rowSelection?.length > 1 ? 'Activate all' : 'Activate'}>
						<Button
							type="button"
							sx={{
								py: '5px',
								color: '#fff',
								fontSize: '13px',
								lineHeight: '22px',
								fontWeight: '500',
								borderRadius: '4px',
								transition: 'all 0.1s',
								backgroundColor: theme.palette.lynTheme.success,

								'&:hover': {
									transform: 'scale(1.05)',
									backgroundColor: theme.palette.lynTheme.success,
								},
							}}
							onClick={() => {
								activeDeactivateHandle('Inactive');
							}}
						>
							{rowSelection?.length > 1 ? 'Activate all' : 'Activate'}
						</Button>
					</Tooltip>
				)}

				{!['Inactive', 'Worklist', 'Drafts']?.includes(selectedTabName) && (
					<Tooltip title={rowSelection?.length > 1 ? 'Deactivate all' : 'Deactivate'}>
						<Button
							type="button"
							sx={{
								py: '6px',
								color: '#fff',
								fontSize: '13px',
								lineHeight: '22px',
								fontWeight: '500',
								borderRadius: '4px',
								transition: 'all 0.1s',
								backgroundColor: theme.palette.lynTheme.submit,

								'&:hover': {
									transform: 'scale(1.05)',
									backgroundColor: theme.palette.lynTheme.submit,
								},
							}}
							onClick={() => {
								activeDeactivateHandle('Active');
							}}
						>
							{rowSelection?.length > 1 ? 'Deactivate all' : 'Deactivate'}
						</Button>
					</Tooltip>
				)}

				{screen === 'Lease Console' && (
					<Tooltip title={rowSelection?.length > 1 ? 'Submit all' : 'Submit'}>
						<Button
							type="button"
							sx={{
								py: '6px',
								color: '#fff',
								fontSize: '13px',
								lineHeight: '22px',
								fontWeight: '500',
								borderRadius: '4px',
								transition: 'all 0.1s',
								backgroundColor: theme.palette.lynTheme.success,

								'&:hover': {
									transform: 'scale(1.05)',
									backgroundColor: theme.palette.lynTheme.success,
								},
							}}
							onClick={() => {
								submitHandle();
							}}
						>
							{rowSelection?.length > 1 ? 'Submit all' : 'Submit'}
						</Button>
					</Tooltip>
				)}

				{bulkDelete && (
					<Tooltip title={rowSelection?.length > 1 ? 'Delete all' : 'Delete'}>
						<Button
							type="button"
							sx={{
								py: '6px',
								color: '#fff',
								fontSize: '13px',
								lineHeight: '22px',
								fontWeight: '500',
								borderRadius: '4px',
								transition: 'all 0.1s',
								backgroundColor: theme.palette.lynTheme.delete,

								'&:hover': {
									transform: 'scale(1.05)',
									backgroundColor: theme.palette.lynTheme.delete,
								},
							}}
							onClick={() => {
								if (typeof deleteHandle === 'function') {
									let arr = rowSelection?.map((item) => item?.original);

									deleteHandle(arr);
								}
							}}
						>
							{rowSelection?.length > 1 ? 'Delete all' : 'Delete'}
						</Button>
					</Tooltip>
				)}
			</Box>
		);
	};

	// const renderActionButtons = () => {
	// 	if (actionButtons !== undefined) {
	// 		return (
	// 			<Box
	// 				content="div"
	// 				sx={{
	// 					display: 'flex',
	// 					flexWrap: 'wrap',
	// 					alignItems: 'center',
	// 					gap: { xs: '6px', md: '8px' },
	// 				}}
	// 			>
	// 				{actionButtons.map((button, index) => (
	// 					<Tooltip key={index} title={button.disabled ? button.disabledTextTooltip : button.title}>
	// 						<Button
	// 							type="primary"
	// 							style={{
	// 								margin: '3px',
	// 								backgroundColor: button.disabled ? 'gray' : button.color,
	// 								color: button.disabled ? 'white' : (button?.textColor ?? '#000000'),
	// 								padding: '6px 8px',
	// 							}}
	// 							disabled={button.disabled}
	// 							onClick={(e) => {
	// 								e.stopPropagation();
	// 								if (!button.disabled && typeof button.onClick === 'function') {
	// 									button.onClick();
	// 								}
	// 							}}
	// 						>
	// 							{rowSelection?.length > 1 ? button.title + ' all' : button.title}
	// 						</Button>
	// 					</Tooltip>
	// 				))}
	// 			</Box>
	// 		);
	// 	}

	// 	if (customButton && customButton.length > 0) {
	// 		return (
	// 			<Box
	// 				content="div"
	// 				sx={{
	// 					display: 'flex',
	// 					flexWrap: 'wrap',
	// 					alignItems: 'center',
	// 					gap: { xs: '6px', md: '8px' },
	// 				}}
	// 			>
	// 				{customButton.map((button, index) => (
	// 					<Tooltip key={index} title={button.disabled ? button.disabledTextTooltip : button.title}>
	// 						<Button
	// 							type="primary"
	// 							disabled={button.disabled}
	// 							style={{
	// 								fontSize: '14px',
	// 								lineHeight: '22px',
	// 								padding: '6px 8px',
	// 								backgroundColor: button.disabled ? 'gray' : button.color,
	// 								color: button.disabled ? 'white' : (button?.textColor ?? '#000000'),
	// 							}}
	// 							onClick={(e) => {
	// 								e.stopPropagation();

	// 								if (!button.disabled && typeof button.onClick === 'function') {
	// 									button.onClick();
	// 								}
	// 							}}
	// 						>
	// 							{rowSelection?.length > 1 ? button.title + ' all' : button.title}
	// 						</Button>
	// 					</Tooltip>
	// 				))}
	// 			</Box>
	// 		);
	// 	}

	// 	return (
	// 		<Box
	// 			content="div"
	// 			sx={{
	// 				display: 'flex',
	// 				flexWrap: 'wrap',
	// 				alignItems: 'center',
	// 				gap: { xs: '6px', md: '8px' },
	// 			}}
	// 		>
	// 			{!['Active', 'Worklist', 'Drafts']?.includes(selectedTabName) && (
	// 				<Tooltip title={rowSelection?.length > 1 ? 'Activate all' : 'Activate'}>
	// 					<Button
	// 						type="primary"
	// 						style={{
	// 							margin: '3px',
	// 							backgroundColor: theme.palette.lynTheme.success,
	// 							color: 'white',
	// 						}}
	// 						onClick={(e) => {
	// 							e.stopPropagation();
	// 							activeDeactivateHandle('Inactive');
	// 						}}
	// 					>
	// 						{rowSelection?.length > 1 ? 'Activate all' : 'Activate'}
	// 					</Button>
	// 				</Tooltip>
	// 			)}

	// 			{!['Inactive', 'Worklist', 'Drafts']?.includes(selectedTabName) && (
	// 				<Tooltip title={rowSelection?.length > 1 ? 'Deactivate all' : 'Deactivate'}>
	// 					<Button
	// 						type="primary"
	// 						style={{
	// 							margin: '3px',
	// 							backgroundColor: theme.palette.lynTheme.submit,
	// 							color: 'white',
	// 						}}
	// 						onClick={(e) => {
	// 							e.stopPropagation();
	// 							activeDeactivateHandle('Active');
	// 						}}
	// 					>
	// 						{rowSelection?.length > 1 ? 'Deactivate all' : 'Deactivate'}
	// 					</Button>
	// 				</Tooltip>
	// 			)}

	// 			{screen === 'Lease Console' && (
	// 				<Tooltip title={rowSelection?.length > 1 ? 'Submit all' : 'Submit'}>
	// 					<Button
	// 						type="primary"
	// 						style={{
	// 							margin: '3px',
	// 							backgroundColor: theme.palette.lynTheme.success,
	// 							color: 'white',
	// 						}}
	// 						onClick={(e) => {
	// 							e.stopPropagation();
	// 							submitHandle();
	// 						}}
	// 					>
	// 						{rowSelection?.length > 1 ? 'Submit all' : 'Submit'}
	// 					</Button>
	// 				</Tooltip>
	// 			)}

	// 			{bulkDelete && (
	// 				<Tooltip title={rowSelection?.length > 1 ? 'Delete all' : 'Delete'}>
	// 					<Button
	// 						type="primary"
	// 						style={{
	// 							margin: '3px',
	// 							backgroundColor: theme.palette.lynTheme.delete,
	// 							color: 'white',
	// 						}}

	// 						onClick={(e) => {
	// 							e.stopPropagation();
	// 							if (typeof deleteHandle === 'function') {
	// 								let arr = rowSelection?.map((item) => item?.original);
	// 								deleteHandle(arr);
	// 							}
	// 						}}
	// 					>
	// 						{rowSelection?.length > 1 ? 'Delete all' : 'Delete'}
	// 					</Button>
	// 				</Tooltip>
	// 			)}
	// 		</Box>
	// 	);
	// };

	const styles = {
		table: { ...tableVariants.default.table, ...tableVariants[variant]?.table },
		pagination: { ...tableVariants.default.pagination, ...tableVariants[variant]?.pagination },
		tableBodyRow: { ...tableVariants.default.tableBodyRow, ...tableVariants[variant]?.tableBodyRow },
		emptyTableBox: { ...tableVariants.default.emptyTableBox, ...tableVariants[variant]?.emptyTableBox },
		tableMainCard: { ...tableVariants.default.tableMainCard, ...tableVariants[variant]?.tableMainCard },
		paginationBox: { ...tableVariants.default.paginationBox, ...tableVariants[variant]?.paginationBox },
		tableHeaderRow: { ...tableVariants.default.tableHeaderRow, ...tableVariants[variant]?.tableHeaderRow },
		tableHeaderCell: (column) => ({ ...tableVariants.default.tableHeaderCell(column), ...tableVariants[variant]?.tableHeaderCell(column) }),
		tableHeaderCellBox: (column) => ({
			...tableVariants.default.tableHeaderCellBox(column),
			...tableVariants[variant]?.tableHeaderCellBox(column),
		}),
		tableBodyCell: (cell) => ({ ...tableVariants.default.tableBodyCell(cell), ...tableVariants[variant]?.tableBodyCell(cell) }),
		tableBodyCellBox: (cell) => ({ ...tableVariants.default.tableBodyCellBox(cell), ...tableVariants[variant]?.tableBodyCellBox(cell) }),
		tableContainer: (pagination) => ({
			...tableVariants.default.tableContainer(pagination),
			...tableVariants[variant]?.tableContainer(pagination),
		}),
	};

	const toolbarContent = (
		<Box
			content="div"
			sx={{
				gap: '8px',
				display: 'flex',
				justifyContent: 'space-between',
				flexWrap: matchDownSM ? 'wrap' : 'no-wrap',
				width: '100%',
			}}
		>
			{columnButton && (
				<SelectVisibleColumn
					allColumns={allColumns}
					visibleColumns={headerGroups[0]?.headers}
					getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
				/>
			)}

			{matchDownSM && exportButton && <ExportButton handleDataExport={dataExport} />}

			{rowSelection?.length > 0 && balkAction && (
				<Box
					content="div"
					sx={{
						gap: '8px',
						display: 'flex',
						flexWrap: 'wrap',
						alignItems: 'center',
						justifyContent: 'space-between',
						width: { xs: '100%', md: 'fit-content' },
					}}
				>
					<>
						<div>
							<b style={{ fontSize: '14px' }}>{rowSelection?.length}</b> {rowSelection?.length > 1 ? 'rows' : 'row'} selected
						</div>

						{renderActionButtons()}
					</>

					{!matchDownSM && exportButton && <ExportButton handleDataExport={dataExport} />}
				</Box>
			)}

			{!matchDownSM && exportButton && rowSelection?.length === 0 && <ExportButton handleDataExport={dataExport} />}
		</Box>
	);

	return (
		<>
			{externalToolbarHost ? createPortal(toolbarContent, externalToolbarHost) : toolbarContent}

			<MainCard sx={styles.tableMainCard} content={false}>
				<TableContainer sx={styles.tableContainer(paginationEnabled)}>
					<Table stickyHeader {...getTableProps()} sx={styles.table}>
						<TableHead>
							{headerGroups?.map((headerGroup, indexRow) => (
								<TableRow {...headerGroup?.getHeaderGroupProps()} key={indexRow + 'R'} sx={styles.tableHeaderRow}>
									{headerGroup?.headers.map((column, indexCol) => (
										<TableCell {...column?.getHeaderProps()} key={indexRow + '_' + indexCol} sx={styles.tableHeaderCell(column)}>
											<Box sx={styles.tableHeaderCellBox(column)} {...column.getSortByToggleProps()}>
												{column?.render('Header')}
												{column?.canSort && <SortingIconGroup sortingProps={column} key={indexRow + '_' + indexCol} />}
											</Box>
										</TableCell>
									))}
								</TableRow>
							))}

							{headerGroups?.map((headerGroup, index) => (
								<TableRow {...headerGroup.getHeaderGroupProps()} sx={{ '& .MuiTableCell-root': { top: '40px' } }} key={index}>
									{headerGroup.headers.map((column, nestedIndex) =>
										column?.id === 'Selection' || column?.id === 'Sl' ? (
											<TableCell
												key={index + '_' + nestedIndex}
												sx={{
													backgroundColor: tableDesign.background.primary,

													...(column?.sticky === 'right' && {
														right: 0,
														zIndex: 2,
														position: 'sticky',
													}),
												}}
											></TableCell>
										) : (
											<TableCell
												{...column.getHeaderProps()}
												key={index + '_' + nestedIndex}
												sx={{
													p: tableDesign.padding.secondary,
													backgroundColor: tableDesign.background.primary,

													'& .ant-input-affix-wrapper': { border: '1px solid #ced4da !important' },

													...(column?.sticky === 'right' && {
														right: 0,
														zIndex: 2,
														position: 'sticky',
													}),
												}}
											>
												{column.canFilter ? column.render('Filter') : null}
											</TableCell>
										)
									)}
								</TableRow>
							))}
						</TableHead>

						<TableBody
							key="table_body2"
							{...getTableBodyProps()}
							sx={{
								'& tr:nth-of-type(even)': {
									backgroundColor: `${tableDesign.tableRowBackgroundColor.primary} !important`,
								},
							}}
						>
							{rowsToRender?.length > 0 ? (
								rowsToRender?.map((row, indexRow) => {
									prepareRow(row);

									return (
										<TableRow
											id={row.id}
											{...row?.getRowProps()}
											key={indexRow}
											sx={styles.tableBodyRow}
											onClick={() => memorizeClickedItem(row.id)}
										>
											{row?.cells?.map((cell, index) => (
												<TableCell
													sx={styles.tableBodyCell(cell)}
													{...cell.getCellProps()}
													key={indexRow + '_' + index}
													onClick={() => {
														if (!row?.cells[index]?.column?.onClicked) {
															if (cell?.column?.id !== 'action') {
																onClickHandler(row.original);
															}
														}
													}}
												>
													{cell?.column?.id === 'Selection' || cell?.column?.id === 'Sl' ? (
														// <Box
														// 	component="div"
														// 	sx={{
														// 		padding: '4px 16px',
														// 		textAlign: cell?.column?.cellAlign || 'left',
														// 	}}
														// >
														<>{cell.render('Cell')}</>
													) : (
														// </Box>
														// <Box component="div" sx={styles.tableBodyCellBox(cell)}>
														<>{cell.render('Cell')}</>
														// </Box>
													)}
												</TableCell>
											))}
										</TableRow>
									);
								})
							) : (
								<TableRow>
									<TableCell colSpan={columns?.length} sx={styles.tableBodyCell}>
										<Box component="div" sx={styles.emptyTableBox}>
											<Empty />
										</Box>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				{paginationEnabled && (
					<Box sx={styles.paginationBox}>
						<TablePagination
							{...{
								pathname,
								state: state,
								gotoPage: gotoPage,
								setPageSize: setPageSize,
								count: filteredRows?.length,
							}}
							sx={styles.pagination}
						/>
					</Box>
				)}
			</MainCard>
		</>
	);
};

export default ComptrollerDataTable;
